import React from 'react'

import { cn } from '@/utilities/ui'

// Inline SVG text, not the design's raster, so it stays crisp and picks up the page's DM Mono.
// MatrixRain draws the falling matrix. See docs/unveiling.md#backgrounds-and-imagery.

const VIEW_W = 1440
const VIEW_H = 1191

/** Phrases transcribed from the Figma field, positioned as fractions of its box. */
/*
 * Two entries here used to read '07231928'. That is Vera Rubin's birthdate, and it was also the
 * gate's card number - so the gate printed its own passcode into the background at 24% opacity and
 * into the page source. The replacements are random and deliberately alphanumeric: the keypad and
 * the unlock endpoint both accept digits only, so a token containing letters cannot be a valid
 * code however it is read off the screen. Do not put a bare eight-digit number back here.
 */
const PHRASES: { x: number; y: number; text: string; aurora?: boolean }[] = [
  { x: 0.17, y: 0.015, text: '52V' },
  { x: 0.24, y: 0.022, text: 'THE UNVEILING', aurora: true },
  { x: 0.25, y: 0.037, text: 'ENTER THE NUMBER' },
  { x: 0.955, y: 0.04, text: 'LIQUID' },
  { x: 0.46, y: 0.13, text: 'CPX' },
  { x: 0.745, y: 0.098, text: 'THE MASS WAS MISSING' },
  { x: 0.865, y: 0.128, text: '-1580 TB/S' },
  { x: 0.265, y: 0.155, text: 'INVITE ONLY' },
  { x: 0.115, y: 0.172, text: '52V' },
  { x: 0.525, y: 0.172, text: 'RUBIN' },
  { x: 0.295, y: 0.19, text: 'YB4YWL9W' },
  { x: 0.825, y: 0.19, text: '23.07.1928' },
  { x: 0.045, y: 0.202, text: 'CIVO' },
  { x: 0.405, y: 0.202, text: 'HERTFORDSHIRE' },
  { x: 0.545, y: 0.202, text: 'VERA' },
  { x: 0.125, y: 0.215, text: 'CIVO' },
  { x: 0.335, y: 0.216, text: '1580 TB/S' },
  { x: 0.335, y: 0.231, text: '51.9022N' },
  { x: 0.465, y: 0.231, text: '3600 PFLOPS', aurora: true },
  { x: 0.615, y: 0.231, text: '3.6 TB/S' },
  { x: 0.055, y: 0.335, text: 'ACCESS' },
  { x: 0.835, y: 0.335, text: 'LIQUID' },
  { x: 0.005, y: 0.378, text: '1580 TB/S' },
  { x: 0.945, y: 0.371, text: 'NVL72' },
  { x: 0.008, y: 0.4, text: '72 GPU' },
  { x: 0.0, y: 0.441, text: 'HBM4' },
  { x: 0.825, y: 0.487, text: '3600 PFLOPS', aurora: true },
  { x: 0.16, y: 0.547, text: 'HBM4' },
  { x: 0.935, y: 0.547, text: '5000A' },
  { x: 0.21, y: 0.62, text: 'F31XLL7W' },
  { x: 0.205, y: 0.652, text: 'ACCESS' },
  { x: 0.925, y: 0.712, text: '1928' },
  { x: 0.735, y: 0.695, text: '1928' },
  { x: 0.25, y: 0.727, text: '27U' },
  { x: 0.675, y: 0.742, text: 'EIGHT DIGITS' },
  { x: 0.375, y: 0.769, text: '1580 TB/S' },
  { x: 0.17, y: 0.789, text: 'HERTFORDSHIRE' },
  { x: 0.515, y: 0.802, text: 'T-MINUS' },
  { x: 0.515, y: 0.818, text: '20.7TB' },
  { x: 0.145, y: 0.859, text: '+3.6 TB/S' },
  { x: 0.325, y: 0.847, text: 'LIQUID' },
  { x: 0.825, y: 0.862, text: 'CIVO' },
  { x: 0.285, y: 0.875, text: '51.9022N' },
  { x: 0.355, y: 0.875, text: '3600 PFLOPS' },
  { x: 0.065, y: 0.895, text: '3.6 TB/S' },
  { x: 0.35, y: 0.893, text: '23.07.1928 VERA RUBIN' },
  { x: 0.225, y: 0.91, text: 'THE MASS WAS MISSING' },
  { x: 0.385, y: 0.925, text: '1928' },
  { x: 0.565, y: 0.939, text: 'THE UNVEILING', aurora: true },
  { x: 0.055, y: 0.955, text: '5000A' },
  { x: 0.195, y: 0.955, text: '52V' },
  { x: 0.055, y: 0.971, text: 'SOVEREIGN', aurora: true },
  { x: 0.705, y: 0.985, text: '72 GPU' },
]

export function AsciiField({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      focusable="false"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="xMidYMid slice"
      className={cn('font-[family-name:var(--font-vr-mono)]', className)}
    >
      <g fontSize={11} letterSpacing={1.6}>
        {PHRASES.map(({ x, y, text, aurora }, i) => (
          <text
            key={`${text}-${i}`}
            x={x * VIEW_W}
            y={y * VIEW_H}
            fill={aurora ? '#00FF6F' : '#D1CCBE'}
            // lifted from the design's values: against the animated rain the words need more
            // weight to read as embedded messages rather than disappear into the noise
            opacity={aurora ? 0.34 : 0.24}
          >
            {text}
          </text>
        ))}
      </g>
    </svg>
  )
}
